import { defineStore, acceptHMRUpdate } from "pinia";
import Api from "../utils/api";

export const useFormFieldStore = defineStore("form_field_store", {
  persist: true,
  state: () => ({
    formFieldList: {} // Use an object to store data with keys as IDs
  }),
  actions: {
    async getFormFieldById(id) {
      // Check if the data already exists in the list
      if (this.formFieldList[id]) {
        return this.formFieldList[id];
      }

      // Fetch from API if not already present
      const _fieldRes = await Api.FormFields.getFormFieldById(id);
      if (_fieldRes.error) {
        throw _fieldRes.error;
      }
      // Add the fetched data to the existing list
      this.updateFormFieldFromList(_fieldRes.data);
      return _fieldRes.data;
    },
    async updateFormFieldFromList(data) {
      // Update the object where key is the ID
      if (data && data.id) {
        this.formFieldList[data.id] = {
          ...this.formFieldList[data.id],
          ...data
        };
      } else {
        console.warn(
          "Invalid data format. Data must include an `id` property."
        );
      }
    },
    async getFormFieldFromList(id) {
      // Return the object by key or null if it doesn't exist
      return this.formFieldList[id] || null;
    },
    /**
     * The form_fields catalog — the builder needs the field_type for each
     * field_id.
     *
     * @returns {Promise<{byId: Map, byType: Map, raw: Array}>}
     */
    async loadFieldCatalog() {
      const result = await Api.FormFields.getFormFieldsList({ fetchAll: true });
      const rows = result?.data ?? [];
      // Index by id for O(1) lookup
      const byId = new Map();
      for (const row of rows) byId.set(row.id, row);
      // Also index by field_type → first matching id (used when creating new fields)
      const byType = new Map();
      for (const row of rows) {
        if (!byType.has(row.field_type)) byType.set(row.field_type, row);
      }
      return { byId, byType, raw: rows };
    },
    async resetFieldList() {
      this.$reset();
    }
  },
  $reset() {
    this.formFieldList = {}; // Reset formFieldList to an empty object
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFormFieldStore, import.meta.hot));
}
